import React from 'react'

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

export type PhotoSlot = {
  id:       string
  label:    string
  icon?:    string                               // emoji (optionnel)
  svgIcon?: 'bench' | 'cargo' | 'bench+cargo'   // SVG custom (optionnel)
  order:    number
  required?: boolean
  vp_x?:    number
  vp_y?:    number
  vu_x?:    number
  vu_y?:    number
}

// ─────────────────────────────────────────────────────────────────────────────
// SVG ICONS
// ─────────────────────────────────────────────────────────────────────────────

const BenchIcon = ({ color = 'currentColor', size = 40 }: { color?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 22 C6 14 10 11 24 11 C38 11 42 14 42 22 L42 25 L6 25 Z"
      fill={color} opacity="0.85"
    />
    <rect x="4" y="25" width="40" height="9" rx="4" fill={color} />
    <path d="M10 34 L8 44" stroke={color} strokeWidth="3.5" strokeLinecap="round" opacity="0.75"/>
    <path d="M38 34 L40 44" stroke={color} strokeWidth="3.5" strokeLinecap="round" opacity="0.75"/>
    <rect x="10" y="6" width="8" height="8" rx="3" fill={color} opacity="0.7"/>
    <rect x="30" y="6" width="8" height="8" rx="3" fill={color} opacity="0.7"/>
    <line x1="24" y1="11" x2="24" y2="34" stroke={color} strokeWidth="1.5" opacity="0.2"/>
  </svg>
)

const CargoIcon = ({ color = 'currentColor', size = 40 }: { color?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="8" width="42" height="34" rx="4"
      fill={color} opacity="0.12" stroke={color} strokeWidth="2.5"/>
    <line x1="3"  y1="8"  x2="9"  y2="14" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.6"/>
    <line x1="45" y1="8"  x2="39" y2="14" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.6"/>
    <line x1="9"  y1="14" x2="39" y2="14" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.6"/>
    <rect x="9" y="14" width="30" height="22" rx="2"
      fill={color} opacity="0.08" stroke={color} strokeWidth="1.5" strokeDasharray="3 2"/>
    <line x1="9"  y1="36" x2="39" y2="36" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.7"/>
    <line x1="11" y1="28" x2="37" y2="28" stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity="0.3"/>
    <line x1="11" y1="32" x2="37" y2="32" stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity="0.3"/>
    <rect x="3"  y="40" width="42" height="5" rx="2" fill={color} opacity="0.7"/>
    <rect x="20" y="41" width="8"  height="2.5" rx="1.2" fill={color} opacity="0.3"/>
  </svg>
)

// ─────────────────────────────────────────────────────────────────────────────
// SLOT ICON — dispatcher emoji | SVG simple | SVG fusionné
// ─────────────────────────────────────────────────────────────────────────────

export const SlotIcon = ({
  slot,
  color = 'white',
  size  = 40,
}: {
  slot:   Pick<PhotoSlot, 'icon' | 'svgIcon'> | null
  color?: string
  size?:  number
}) => {
  if (!slot) return null

  // bench + cargo côte à côte
  if (slot.svgIcon === 'bench+cargo') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <BenchIcon color={color} size={size} />
        <span style={{ color, opacity: 0.5, fontSize: size * 0.55, lineHeight: 1, fontWeight: 300 }}>
          +
        </span>
        <CargoIcon color={color} size={size} />
      </div>
    )
  }

  if (slot.svgIcon === 'bench') return <BenchIcon color={color} size={size} />
  if (slot.svgIcon === 'cargo') return <CargoIcon color={color} size={size} />

  if (slot.icon) return <span style={{ fontSize: size * 0.85, lineHeight: 1 }}>{slot.icon}</span>

  return null
}

// ─────────────────────────────────────────────────────────────────────────────
// PHOTO_SLOTS — 12 zones, ordre de prise de vue
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_PHOTO_SLOTS: PhotoSlot[] = [
  { id: 'rear',           label: 'Arrière',                    icon: '⬇️',             order: 0,  vp_x: 50, vp_y: 96, vu_x: 50, vu_y: 97 },
  { id: 'rear-right',     label: 'Arrière Droit',              icon: '↙️',             order: 1,  vp_x: 83, vp_y: 78, vu_x: 86, vu_y: 88 },
  { id: 'right',          label: 'Latéral Droit',              icon: '➡️',             order: 2,  vp_x: 97, vp_y: 50, vu_x: 97, vu_y: 52 },
  { id: 'front-right',    label: 'Avant Droit',                icon: '↗️',             order: 3,  vp_x: 83, vp_y: 21, vu_x: 86, vu_y: 14 },
  { id: 'front',          label: 'Avant',                      icon: '⬆️',             order: 4,  vp_x: 50, vp_y: 3,  vu_x: 50, vu_y: 3  },
  { id: 'front-left',     label: 'Avant Gauche',               icon: '↖️',             order: 5,  vp_x: 17, vp_y: 21, vu_x: 14, vu_y: 14 },
  { id: 'left',           label: 'Latéral Gauche',             icon: '⬅️',             order: 6,  vp_x: 3,  vp_y: 50, vu_x: 3,  vu_y: 52 },
  { id: 'rear-left',      label: 'Arrière Gauche',             icon: '↘️',             order: 7,  vp_x: 17, vp_y: 78, vu_x: 14, vu_y: 88 },
  { id: 'dashboard',      label: 'Compteur',                   icon: '🔢',             order: 8,  vp_x: 62, vp_y: 38, vu_x: 62, vu_y: 22 },
  { id: 'interior-front', label: 'Intérieur Avant',            icon: '💺',             order: 9,  vp_x: 38, vp_y: 50, vu_x: 38, vu_y: 30 },
  { id: 'interior-rear',  label: 'Intérieur Arrière / Caisse', svgIcon: 'bench+cargo', order: 10, vp_x: 62, vp_y: 62, vu_x: 62, vu_y: 65 },
  { id: 'spare',          label: 'Roue de secours / Kit',      icon: '🔧',             order: 11, vp_x: 38, vp_y: 72, vu_x: 38, vu_y: 80 },
]

// Type des IDs — rétrocompatible avec l'ancien PhotoSlotId
export type PhotoSlotId = typeof DEFAULT_PHOTO_SLOTS[number]['id']

// Ref live — mise à jour par useSettingsStore.fetchSlots()
// PhotoScreen.tsx continue d'importer PHOTO_SLOTS sans aucun changement
export let PHOTO_SLOTS: PhotoSlot[] = [...DEFAULT_PHOTO_SLOTS]

export function setLivePhotoSlots(slots: PhotoSlot[]) {
  PHOTO_SLOTS = [...slots].sort((a, b) => a.order - b.order)
}

// ─────────────────────────────────────────────────────────────────────────────
// FUEL
// ─────────────────────────────────────────────────────────────────────────────

export const FUEL_LABELS = ['0%', '10%', '20%', '30%', '40%', '50%', '60%', '70%', '80%', '90%', '100%']
export const FUEL_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308',
  '#dcfce7', '#84cc16', '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#60a5fa'
]